/**
 * Mock briefing API — seeded data for VITE_API_MODE=mock.
 */

import type {
  BriefingHistoryResponse,
  FounderBriefing,
} from '../types';

const BASE_NOW = Date.now();
const DAY = 86_400_000;

function makeBriefing(daysAgo: number, seq: number): FounderBriefing {
  const ts = new Date(BASE_NOW - daysAgo * DAY).toISOString();
  const quiet = daysAgo > 2;
  return {
    briefing_id: `briefing-mock-${seq}`,
    generated_at: ts,
    sequence: 200 + seq,
    overnight_summary: quiet
      ? [
          'Quiet night — no new OMEGA log activity.',
          'No brain executions recorded.',
          'No new decisions require your action.',
        ]
      : [
          '47 OMEGA log entries recorded in the last window.',
          '12 brain execution(s) — OMEGA is active.',
          '2 decision(s) arrived requiring your action.',
        ],
    decisions_count: quiet ? 0 : 2,
    decisions_top3: quiet
      ? []
      : [
          {
            decision_id: 'd-001',
            question: 'Should OMEGA expand into the EU market with the tokenized-bond product?',
            confidence: 'high',
            expires_at: new Date(BASE_NOW + 20 * 3600_000).toISOString(),
          },
          {
            decision_id: 'd-002',
            question: 'Approve rate increase for anomaly-detector specialist resource limits?',
            confidence: 'medium',
            expires_at: new Date(BASE_NOW + 36 * 3600_000).toISOString(),
          },
        ],
    threats_total: quiet ? 0 : 1,
    threats_critical: [],
    threats_all_handled: true,
    revenue: {
      subscriptions_usd_cents: quiet ? 0 : 42000,
      royalties_usd_cents: quiet ? 0 : 8750,
      churn_usd_cents: quiet ? 0 : 5000,
      net_usd_cents: quiet ? 0 : 45750,
    },
    memory_insights: quiet
      ? []
      : [
          {
            memory_id: 'm-001',
            summary: 'EU tokenized-bond product shows strong search intent signal in Germany and France.',
            tags: ['eu-market', 'tokenized-bond', 'market-research'],
          },
          {
            memory_id: 'm-002',
            summary: 'Anomaly detector false-positive rate drops 40% after baseline recalibration.',
            tags: ['anomaly-detector', 'performance', 'defense'],
          },
          {
            memory_id: 'm-003',
            summary: 'Pattern scanner identified 3 new product opportunities in the DeFi space.',
            tags: ['defi', 'discovery', 'pattern-scanner'],
          },
        ],
    recommendation: quiet
      ? 'No urgent actions needed. OMEGA is operating normally.'
      : 'Review pending decision: "Should OMEGA expand into the EU market with the tokenized-bond product?"',
    activity: quiet
      ? []
      : [
          { entry_type: 'brain_execution', count: 12 },
          { entry_type: 'trigger_fired', count: 8 },
          { entry_type: 'entity', count: 7 },
          { entry_type: 'decision_pending', count: 2 },
          { entry_type: 'attestation', count: 5 },
          { entry_type: 'consent', count: 3 },
          { entry_type: 'memory_expired', count: 1 },
        ],
  };
}

const SEEDED: FounderBriefing[] = [
  makeBriefing(0, 0),   // today
  makeBriefing(1, 1),
  makeBriefing(2, 2),
  makeBriefing(3, 3),
  makeBriefing(5, 4),
  makeBriefing(7, 5),
  makeBriefing(10, 6),
  makeBriefing(13, 7),
];

export async function getBriefingToday(): Promise<FounderBriefing> {
  return SEEDED[0]!;
}

export async function getBriefingHistory(days = 7): Promise<BriefingHistoryResponse> {
  const cutoff = BASE_NOW - days * DAY;
  const filtered = SEEDED.filter(
    (b) => new Date(b.generated_at).getTime() >= cutoff,
  );
  return { briefings: filtered, total: filtered.length };
}
